import { Type } from 'typebox';
import { getBlobFilename, isRecord } from '@tadpoles/common';
import { encodeMultipartManifest } from '../core/multipart-manifest.js';
export const MultipartAttachmentUploadUrlRequest = Type.Object({
    manifest: Type.String({ minLength: 1 }),
    id: Type.String({ minLength: 1 })
});
export const MultipartAttachmentUploadUrlPayload = Type.Object({
    url: Type.String({ minLength: 1 })
});
export class MultipartClient {
    #httpClient;
    constructor(httpClient) {
        this.#httpClient = httpClient;
    }
    async postMultipart(options) {
        if (!this.#containsAttachmentArray(options.body, options.attachments)) {
            throw new Error('Multipart request body must include the provided attachments array');
        }
        const preparedAttachments = this.#prepareAttachments(options.attachments);
        const encodedManifest = preparedAttachments.manifest
            ? this.#encodeManifest(preparedAttachments.manifest)
            : undefined;
        const uploadCompletionWithSignal = preparedAttachments.manifest && preparedAttachments.pendingUploads.length > 0
            ? this.#uploadPendingFiles(preparedAttachments.manifest, preparedAttachments.pendingUploads, options.signal)
            : undefined;
        if (uploadCompletionWithSignal) {
            void uploadCompletionWithSignal.catch(() => { });
        }
        const response = await this.#httpClient.requestEnvelope({
            method: 'POST',
            path: options.path,
            acceptedErrorCodes: ['multipart_processing'],
            headers: encodedManifest
                ? {
                    'X-TADPOLES-MULTIPART-MANIFEST': encodedManifest
                }
                : undefined,
            body: options.body,
            bodySchema: options.bodySchema,
            payloadSchema: options.payloadSchema,
            signal: options.signal
        });
        return {
            manifestId: preparedAttachments.manifest?.id,
            payload: response.status === 'ok' ? response.payload : undefined,
            errorCode: response.status === 'error' ? response.error.code : undefined,
            errorMessage: response.status === 'error' ? response.error.message : undefined,
            upload: uploadCompletionWithSignal
                ? {
                    completion: uploadCompletionWithSignal,
                    fileCount: preparedAttachments.pendingUploads.length
                }
                : undefined
        };
    }
    #prepareAttachments(attachments) {
        const mediaIds = [];
        const manifestAttachments = [];
        const manifestFiles = [];
        const pendingUploads = [];
        attachments.forEach((attachment) => {
            if (this.#isExistingAttachment(attachment)) {
                if (attachment.mediaId || attachment.originKey || attachment.file) {
                    throw new Error('Existing attachments cannot also declare upload dependencies');
                }
                return;
            }
            if (this.#isMediaAttachment(attachment)) {
                mediaIds.push(attachment.mediaId);
                return;
            }
            if (this.#isCopyAttachment(attachment)) {
                manifestAttachments.push({
                    key: attachment.originKey,
                    copyUuid: attachment.uuid
                });
                return;
            }
            if (this.#isFileAttachment(attachment)) {
                const filename = attachment.filename ?? getBlobFilename(attachment.file);
                if (!filename) {
                    throw new Error('File attachments must provide a filename');
                }
                manifestFiles.push({
                    id: attachment.uuid,
                    name: `attachment-${attachment.uuid}`,
                    filename
                });
                pendingUploads.push({
                    blob: attachment.file,
                    filename,
                    id: attachment.uuid,
                    mimeType: attachment.mimeType
                });
                return;
            }
            throw new Error('Attachments without keys must declare exactly one dependency: mediaId, originKey, or file');
        });
        const manifest = mediaIds.length > 0 || manifestAttachments.length > 0 || manifestFiles.length > 0
            ? {
                id: crypto.randomUUID(),
                mediaIds: mediaIds.length > 0 ? mediaIds : undefined,
                attachments: manifestAttachments.length > 0 ? manifestAttachments : undefined,
                files: manifestFiles.length > 0 ? manifestFiles : undefined
            }
            : undefined;
        return {
            manifest,
            pendingUploads
        };
    }
    async #uploadPendingFiles(manifest, pendingUploads, signal) {
        const encodedManifest = this.#encodeManifest(manifest);
        await Promise.all(pendingUploads.map(async (pendingUpload) => {
            const uploadUrlPayload = await this.#httpClient.post({
                path: '/attachments/upload/url',
                body: {
                    manifest: encodedManifest,
                    id: pendingUpload.id
                },
                bodySchema: MultipartAttachmentUploadUrlRequest,
                payloadSchema: MultipartAttachmentUploadUrlPayload,
                signal
            });
            await this.#httpClient.sendRaw({
                url: uploadUrlPayload.url,
                headers: {
                    'Content-Type': pendingUpload.blob.type || pendingUpload.mimeType,
                    'x-goog-meta-multipart-manifest': encodedManifest,
                    'x-goog-meta-file-id': pendingUpload.id
                },
                body: pendingUpload.blob,
                signal
            });
        }));
    }
    #encodeManifest(manifest) {
        return encodeMultipartManifest(manifest);
    }
    #containsAttachmentArray(value, attachments) {
        if (value === attachments) {
            return true;
        }
        if (Array.isArray(value)) {
            return value.some((item) => this.#containsAttachmentArray(item, attachments));
        }
        if (!isRecord(value)) {
            return false;
        }
        return Object.values(value).some((item) => this.#containsAttachmentArray(item, attachments));
    }
    #isExistingAttachment(attachment) {
        return typeof attachment.key === 'string';
    }
    #isMediaAttachment(attachment) {
        return (typeof attachment.mediaId === 'string' &&
            typeof attachment.uuid === 'string' &&
            typeof attachment.mimeType === 'string' &&
            typeof attachment.status === 'string');
    }
    #isCopyAttachment(attachment) {
        return (typeof attachment.originKey === 'string' &&
            typeof attachment.uuid === 'string' &&
            typeof attachment.mimeType === 'string' &&
            typeof attachment.status === 'string');
    }
    #isFileAttachment(attachment) {
        return (isRecord(attachment) &&
            attachment.file instanceof Blob &&
            typeof attachment.uuid === 'string' &&
            typeof attachment.mimeType === 'string' &&
            typeof attachment.status === 'string');
    }
}
